import { datadogLogs } from '@datadog/browser-logs';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import {
  calculateSafeDuration,
  getEffectiveStartTime,
} from '@web-app/utils/env.utils';
import { useEffect } from 'react';

const POLLING_INTERVAL_MS = 120_000; // 2 minutes

interface UseDurationPollingArgs {
  roomId: string;
  startTime: Date | null;
  isActive: boolean;
}

const saveDuration = async (
  roomId: string,
  duration: number,
): Promise<void> => {
  const response = await fetch(`/api/session-notes/duration/${roomId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ duration }),
  });

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }
};

export function useDurationPolling({
  roomId,
  startTime,
  isActive,
}: UseDurationPollingArgs): void {
  useEffect(() => {
    if (!isActive || !startTime) {
      return;
    }

    const tick = () => {
      const effectiveStart = getEffectiveStartTime(startTime);
      const minutes = calculateSafeDuration(effectiveStart);

      if (minutes <= 0) {
        return;
      }

      saveDuration(roomId, minutes).catch((error: unknown) => {
        datadogLogs.logger.warn(
          'Duration polling: failed to save',
          callLogContext('duration-tracking', {
            roomId,
            participantType: 'provider',
            polledDuration: minutes,
            error: error instanceof Error ? error.message : String(error),
          }),
        );
      });
    };

    // Immediate first tick closes the blind spot after provider reconnect
    tick();

    const intervalId = setInterval(tick, POLLING_INTERVAL_MS);

    datadogLogs.logger.info(
      'Duration polling: started',
      callLogContext('duration-tracking', {
        roomId,
        participantType: 'provider',
        intervalMs: POLLING_INTERVAL_MS,
      }),
    );

    return () => {
      clearInterval(intervalId);
      datadogLogs.logger.info(
        'Duration polling: stopped',
        callLogContext('duration-tracking', {
          roomId,
          participantType: 'provider',
        }),
      );
    };
  }, [isActive, startTime, roomId]);
}
